<template>
    <v-form
        v-if="company"
        ref="form"
        v-model="valid"
        class="company-profile"
        data-cy="company-profile"
        :disabled="dontAllowEdit"
    >
        <v-row class="company-profile__header" dense>
            <v-col cols="12" sm="6" md="2" class="company-profile__header-left">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col cols="12" sm="6" md="10" class="company-profile__header-right align-center">
                <p
                    v-if="company.id"
                    class="company-profile__header-right-txt width-max-content ml-auto pr-2 text-subtitle-2 text-grey font-weight-regular"
                >
                    {{ $t('id') }}: {{ company.id }}
                </p>
                <v-row v-if="!isMyCompanyPage" dense>
                    <v-switch
                        v-model="company.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4"
                        :label="company.active ? $t('active') : $t('inactive')"
                        data-cy="company-status"
                        hide-details
                        density="compact"
                    />
                </v-row>
            </v-col>
        </v-row>
        <v-row class="company-profile__body">
            <v-col class="company-profile__body-main" cols="12" md="9">
                <v-row>
                    <v-col cols="12" md="3" class="col-title text-h6 text--secondary">
                        {{ $t('general_info') }}
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="company.name[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="company-name"
                                    :label="`${$t('company_name')}*`"
                                    :rules="companyNameRules"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-autocomplete
                                    v-model="company.type"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="company-type"
                                    :items="mapedTypes"
                                    item-title="name"
                                    item-value="value"
                                    :label="`${$t('company_type')}*`"
                                    :disabled="!!company.id || disableType || dontAllowEdit"
                                    :rules="[requiredRule('company_type')]"
                                />
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-text-field
                                    v-model="company.alias"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="alias"
                                    :label="$t('company_alias')"
                                />
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-text-field
                                    v-model="company.companyCode"
                                    readonly
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="company-code"
                                    :label="$t('company_code')"
                                    disabled
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="company.countryId"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="country-id"
                                    :items="countries"
                                    item-title="name"
                                    item-value="id"
                                    clearable
                                    :label="countryLabel"
                                    :rules="company?.type === 'INTERNAL_SUPPLIER' ? [] : [requiredRule('country')]"
                                    @update:model-value="loadCities"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="company.cityId"
                                    :disabled="dontAllowEdit || isCitiesLoading"
                                    :items="cities"
                                    item-title="name"
                                    item-value="id"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="city-id"
                                    clearable
                                    :label="`${$t('city')}`"
                                    :loading="isCitiesLoading"
                                >
                                    <template v-slot:item="{item, props}">
                                        <v-list-item v-bind="props">
                                            <template v-slot:title>
                                                <v-lazy>{{ item.title }}</v-lazy>
                                            </template>
                                        </v-list-item>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="3">
                                <v-text-field
                                    v-model="company.region[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="region"
                                    :label="`${$t('region')}`"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="9">
                                <v-text-field
                                    v-model="company.address[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="address"
                                    :label="`${$t('address')}`"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="3">
                                <v-text-field
                                    v-model="company.postalCode"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="postal-code"
                                    :label="`${$t('postal_code')}`"
                                />
                            </v-col>
                            <v-col cols="12" md="9">
                                <v-text-field
                                    v-model="company.postAddress[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="post-address"
                                    :label="`${$t('postal_address')}`"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="3" class="col-title text-h6 text--secondary">
                        {{ $t('contacts') }}
                        <div class="col-subtitle px-0 font-italic text-subtitle-2">
                            {{ $t('main_contact_person') }}
                        </div>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" md="2">
                                <v-select
                                    v-model="company.contactPerson.prefix"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="contact-person-prefix"
                                    :items="['Mr', 'Ms', 'Mrs']"
                                    :label="$t('salutation')"
                                />
                            </v-col>
                            <v-col cols="12" md="5">
                                <v-text-field
                                    v-model="company.contactPerson.firstName[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="contact-person-first-name"
                                    :label="$t('first_name')"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="5">
                                <v-text-field
                                    v-model="company.contactPerson.lastName[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="contact-person-last-name"
                                    :label="$t('last_name')"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <phone-field
                                    v-model="company.phones[0]"
                                    :label="`${$t('phone')} 1`"
                                    density="compact"
                                    color="primary"
                                    data-cy="phone-1"
                                    :disabled="dontAllowEdit"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <phone-field
                                    v-model="company.phones[1]"
                                    :label="`${$t('phone')} 2`"
                                    density="compact"
                                    color="primary"
                                    data-cy="phone-2"
                                    :disabled="dontAllowEdit"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <phone-field
                                    v-model="company.phones[2]"
                                    :label="`${$t('phone')} 3`"
                                    density="compact"
                                    color="primary"
                                    data-cy="phone-3"
                                    :disabled="dontAllowEdit"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="company.emails[0]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="email-1"
                                    :label="`${$t('email_1')}*`"
                                    :rules="email1Rules"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="company.emails[1]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="email-2"
                                    :label="$t('email_2')"
                                    :rules="[emailRule('email')]"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="company.emails[2]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="email-3"
                                    :label="$t('email_3')"
                                    :rules="[emailRule('email')]"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="company.fax"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="fax"
                                    :label="$t('fax')"
                                />
                            </v-col>
                            <v-col cols="12" md="8">
                                <v-text-field
                                    v-model="company.url"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="url"
                                    :label="$t('url')"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="3" class="col-title text-h6 text--secondary">
                        {{ $t('additional_info') }}
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="company.officialCompanyName[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="official-company-name"
                                    :label="`${$t('company_official_name')}`"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="company.vivaAccountId"
                                    readonly
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="viva-account-id"
                                    :label="$t('viva_account_id')"
                                    class="viva-account-id-readonly"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="6" md="4">
                                <v-text-field
                                    v-model="company.externalCode"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="external-code"
                                    :label="$t('external_code')"
                                />
                            </v-col>
                            <v-col cols="6" md="4">
                                <v-text-field
                                    v-model="company.taxRegistrationReason"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="company-kpp"
                                    :label="$t('company_kpp')"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="6" md="4">
                                <v-text-field
                                    v-model="company.vatNumber"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="vat-number"
                                    :label="`${$t('vat_number')}`"
                                />
                            </v-col>
                            <v-col cols="6" md="4">
                                <v-text-field
                                    v-model="company.iataNumber"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="iata-number"
                                    :label="$t('iata_number')"
                                />
                            </v-col>
                            <v-col md="4">
                                <v-text-field
                                    v-model="company.docexNumber"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="docex_number"
                                    :label="$t('docex_number')"
                                />
                            </v-col>
                        </v-row>
                        <v-row v-if="isShowConsortiumns" dense>
                            <v-col md="4">
                                <v-select
                                    v-model="company.consortium"
                                    density="compact"
                                    :items="consortiumns"
                                    clearable
                                    hide-details
                                    color="primary"
                                    data-cy="consortium"
                                    :label="$t('consortium')"
                                />
                            </v-col>
                            <v-col md="4">
                                <v-select
                                    v-model="company.subconsortium"
                                    density="compact"
                                    :items="subConsortiums"
                                    clearable
                                    hide-details
                                    color="primary"
                                    data-cy="subconsortium"
                                    :label="$t('sub_consortium')"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row v-if="isTO1">
                    <v-col cols="12" md="3" class="col-title text-h6 text--secondary">
                        {{ $t('internal_comments') }}
                        <div class="col-subtitle px-0 font-italic text-subtitle-2">
                            {{ $t('visible_for_to_only') }}
                        </div>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-textarea
                            v-model="company.comments[activeLangCode]"
                            :disabled="dontAllowEdit"
                            variant="outlined"
                            color="primary"
                            data-cy="comments"
                        >
                            <template v-slot:append-inner>
                                <span
                                    v-if="activeLangCode"
                                    class="fi"
                                    :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                />
                            </template>
                        </v-textarea>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="3" class="company-profile__body-sidebar pl-2 order-first order-md-last">
                <v-select
                    v-model="company.language"
                    :items="locales"
                    :label="$t('language')"
                    clearable
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="company-language"
                />
                <div class="relative-position px-2 pb-10">
                    <v-img
                        :src="logo ? logo : $filters.imageUrl(company.logo, 650)"
                        :lazy-src="$filters.srcHost('/placeholder.png')"
                        :max-height="160"
                        class="mb-4"
                    />
                    <v-row>
                        <div class="ml-auto width-max-content">
                            <v-file-input
                                v-model="logoArray"
                                prepend-icon="mdi-pencil"
                                small-chips
                                variant="outlined"
                                density="compact"
                                accept="image/jpg, image/jpeg, image/png"
                                hide-details
                                :label="$t('select_photo')"
                                data-cy="add-logo"
                                class="hide-input"
                                @change="loadFile(logoArray, 'logo')"
                            />
                        </div>
                        <v-icon
                            icon="mdi-close"
                            color="error"
                            data-cy="remove-logo"
                            class="cursor-pointer ml-2"
                            @click="removeFile('logo')"
                        />
                    </v-row>
                </div>
                <div v-if="isMyCompanyPage">
                    <h4 class="text-h6 text--secondary">
                        {{ $t('signature') }}
                    </h4>
                    <div class="relative-position px-2 pb-10">
                        <v-img
                            v-if="company.id"
                            :src="signature ? signature : $filters.imageUrl(company.signature, 650)"
                            :lazy-src="$filters.srcHost('/placeholder.png')"
                            :max-height="160"
                            class="mb-4"
                        />
                        <v-row>
                            <div class="ml-auto width-max-content">
                                <v-file-input
                                    v-model="filesArray"
                                    prepend-icon="mdi-pencil"
                                    small-chips
                                    variant="outlined"
                                    density="compact"
                                    accept="image/jpg, image/jpeg, image/png"
                                    hide-details
                                    :label="$t('select_photo')"
                                    data-cy="avatar"
                                    class="hide-input"
                                    @change="loadFile(filesArray, 'signature')"
                                />
                            </div>
                            <v-icon
                                icon="mdi-close"
                                color="error"
                                data-cy="remove-avatar"
                                class="cursor-pointer ml-2"
                                @click="removeFile('signature')"
                            />
                        </v-row>
                    </div>
                </div>
                <v-btn v-if="false" block>
                    {{ $t('download') }}
                </v-btn>
                <v-select
                    v-if="isCRMExtendedModule && !isAgency && !isTO2 && !isMyCompanyPage"
                    v-model="company.stage"
                    :items="useStages"
                    :label="$t('stage')"
                    clearable
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="stage"
                />
                <v-select
                    v-if="isCRMExtendedModule && !isAgency && !isTO2 && !isMyCompanyPage"
                    v-model="company.priorityLevel"
                    :items="usePriorities"
                    :label="$t('priority')"
                    clearable
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="priority"
                />
                <v-select
                    v-if="isCRMExtendedModule && !isAgency && !isTO2 && !isMyCompanyPage"
                    v-model="company.industry"
                    :items="useIndustries"
                    :label="$t('industry')"
                    clearable
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="industry"
                />
                <v-select
                    v-if="isCRMExtendedModule && !isAgency && !isTO2 && !isMyCompanyPage"
                    v-model="company.companySize"
                    :items="useCompanySize"
                    :label="$t('company_size')"
                    clearable
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="company-size"
                />
                <v-text-field
                    v-model="company.externalId"
                    :label="$t('external_member_id')"
                    type="number"
                    hide-spin-buttons
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="external-member-id"
                />
            </v-col>
        </v-row>
        <v-row no-gutters class="company-profile__footer gc-4">
            <v-btn
                :loading="isLoading"
                :disabled="isLoading || !valid || dontAllowEdit"
                class="btn-save"
                data-cy="save"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
            <v-btn class="btn-cancel" data-cy="cancel" variant="text" @click="cancel">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import PhoneField from '~/components/snippets/forms/PhoneField.vue'
    import type {VForm} from 'vuetify/components'
    import type {CompanyProfileType as BaseCompanyProfileType} from '~/utils/types/account/companies/types'
    import {blobToBase64, clone} from '~/utils/helpers'
    import {initCompanyProfile as baseInitCompanyProfile} from '~/utils/types/account/companies/helpers'
    import {Company} from '~/utils/types/types'
    import {authStore, runtimeStore} from '~/composables/store-accessor'
    import {useFlagIconCode} from '~src/composables/helpers'
    import {companyInfoMessageToCreateCompanyRQ, companyInfoMessageToUpdateCompanyRQ} from '~/utils/api-adapters'

    // Extend the base type to include vivaAccountId
    type CompanyProfileType = BaseCompanyProfileType & {
        vivaAccountId?: string
    }

    // Create extended init function
    const initCompanyProfile = (): CompanyProfileType => ({
        ...baseInitCompanyProfile(),
        vivaAccountId: null,
    })

    @Component({
        components: {PhoneField},
    })
    export class CompanyProfile extends mixins(FormValidationRulesMixin) {
        @Prop({default: () => initCompanyProfile()}) companyData: CompanyProfileType
        @Prop({default: false, type: Boolean}) disableType: boolean
        @Prop({default: false, type: Boolean}) noRedirect: boolean

        types = ['INTERNAL_SUPPLIER', 'TOUR_OPERATOR', 'TOUR_AGENCY', 'CORPORATOR']
        isLoading: boolean = false
        valid: boolean = false
        cities = []
        isCitiesLoading = false
        company = initCompanyProfile()

        activeLangCode: string = 'en'
        signature: string = null
        filesArray = []
        logo: string = null
        logoArray = []

        consortiumns = []
        subConsortiums = []

        async mounted() {
            this.activeLangCode = this.company.language ? this.company.language : this.locale
            this.company = clone(this.companyData)
            if (this.company?.id) {
                this.types = ['INTERNAL_SUPPLIER', 'TOUR_OPERATOR_1LEVEL', 'TOUR_OPERATOR', 'TOUR_AGENCY', 'CORPORATOR']
                await this.form.validate()
                await this.loadCities(this.company.countryId)
            } else {
                if (this.isAgency) {
                    this.types = ['CORPORATOR']
                }
                if (this.isTO2) {
                    this.types = ['TOUR_AGENCY', 'CORPORATOR']
                }
            }
            if (this.isShowConsortiumns) {
                try {
                    this.consortiumns = (await this.$api.consortiums.get()).consortiums
                    this.subConsortiums = (await this.$api.subConsortiums.get()).subConsortiums
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }

            // Force email fields to maintain opacity: 1 programmatically
            this.$nextTick(() => {
                this.enforceEmailFieldOpacity()
            })
        }

        // Force email fields to maintain opacity: 1 programmatically
        enforceEmailFieldOpacity() {
            const emailFields = ['email-1', 'email-2', 'email-3']

            emailFields.forEach(fieldId => {
                const field = document.querySelector(`[data-cy="${fieldId}"]`)
                if (field && field instanceof HTMLElement && field.style) {
                    try {
                        // Set initial opacity for the field
                        field.style.setProperty('opacity', '1', 'important')
                    } catch (e) {
                        // Fallback to direct style assignment
                        try {
                            field.style.opacity = '1'
                        } catch (fallbackError) {}
                    }

                    // CRITICAL: Also target the input element specifically
                    const inputElement = field.querySelector('input')
                    if (inputElement && inputElement instanceof HTMLElement && inputElement.style) {
                        try {
                            // Set inline styles with !important
                            inputElement.style.setProperty('opacity', '1', 'important')
                            inputElement.style.setProperty('transition', 'none', 'important')
                            inputElement.style.setProperty('animation', 'none', 'important')
                        } catch (e) {
                            // Fallback to direct style assignment
                            try {
                                inputElement.style.opacity = '1'
                                inputElement.style.transition = 'none'
                                inputElement.style.animation = 'none'
                            } catch (fallbackError) {}
                        }

                        // Set direct inline style attribute as backup
                        try {
                            inputElement.setAttribute(
                                'style',
                                (inputElement.getAttribute('style') || '') +
                                    '; opacity: 1 !important; transition: none !important; animation: none !important;'
                            )
                        } catch (e) {}
                    }

                    // Create a MutationObserver to watch for style changes
                    const observer = new MutationObserver(mutations => {
                        mutations.forEach(mutation => {
                            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                                const target = mutation.target
                                if (target && target instanceof HTMLElement && target.style) {
                                    try {
                                        if (target.style.opacity !== '1') {
                                            target.style.setProperty('opacity', '1', 'important')

                                            // Force set inline style attribute directly
                                            const currentStyle = target.getAttribute('style') || ''
                                            const newStyle =
                                                currentStyle.replace(/opacity\s*:\s*[^;]+;?/gi, '') +
                                                '; opacity: 1 !important; transition: none !important; animation: none !important;'
                                            target.setAttribute('style', newStyle)
                                        }
                                        // Also disable transitions
                                        if (target.tagName === 'INPUT') {
                                            target.style.setProperty('transition', 'none', 'important')
                                            target.style.setProperty('animation', 'none', 'important')
                                        }
                                    } catch (e) {}
                                }
                            }
                        })
                    })

                    // Start observing the field
                    observer.observe(field, {
                        attributes: true,
                        attributeFilter: ['style'],
                    })

                    // Also observe the input element if it exists
                    if (inputElement) {
                        observer.observe(inputElement, {
                            attributes: true,
                            attributeFilter: ['style'],
                        })
                    }

                    // Also observe parent container
                    const parent = field.closest('.v-col')
                    if (parent && parent instanceof HTMLElement && parent.style) {
                        try {
                            parent.style.setProperty('opacity', '1', 'important')
                        } catch (e) {
                            try {
                                parent.style.opacity = '1'
                            } catch (fallbackError) {}
                        }
                        observer.observe(parent, {
                            attributes: true,
                            attributeFilter: ['style'],
                        })
                    }
                }
            })

            // Set up interval to continuously enforce opacity (fallback)
            setInterval(() => {
                emailFields.forEach(fieldId => {
                    const field = document.querySelector(`[data-cy="${fieldId}"]`)
                    if (field && field instanceof HTMLElement && field.style) {
                        try {
                            // Check field opacity
                            const computedStyle = window.getComputedStyle(field)
                            if (computedStyle.opacity !== '1') {
                                field.style.setProperty('opacity', '1', 'important')
                            }
                        } catch (e) {
                            // Fallback for field opacity
                            try {
                                field.style.opacity = '1'
                            } catch (fallbackError) {}
                        }

                        // CRITICAL: Check input element opacity specifically
                        const inputElement = field.querySelector('input')
                        if (inputElement && inputElement instanceof HTMLElement && inputElement.style) {
                            try {
                                const inputComputedStyle = window.getComputedStyle(inputElement)
                                if (inputComputedStyle.opacity !== '1') {
                                    // Set styles via JavaScript
                                    inputElement.style.setProperty('opacity', '1', 'important')
                                    inputElement.style.setProperty('transition', 'none', 'important')
                                    inputElement.style.setProperty('animation', 'none', 'important')

                                    // Force set inline style attribute directly
                                    const currentStyle = inputElement.getAttribute('style') || ''
                                    const newStyle =
                                        currentStyle.replace(/opacity\s*:\s*[^;]+;?/gi, '') +
                                        '; opacity: 1 !important; transition: none !important; animation: none !important;'
                                    inputElement.setAttribute('style', newStyle)

                                    // Force remove any transition classes
                                    inputElement.classList.remove(
                                        'v-enter-active',
                                        'v-leave-active',
                                        'v-enter-from',
                                        'v-enter-to',
                                        'v-leave-from',
                                        'v-leave-to'
                                    )

                                    // Set CSS custom properties
                                    inputElement.style.setProperty('--v-input-opacity', '1', 'important')
                                    inputElement.style.setProperty('--v-field-opacity', '1', 'important')
                                    inputElement.style.setProperty('--v-text-field-opacity', '1', 'important')
                                }
                            } catch (e) {
                                // Fallback for input element
                                try {
                                    inputElement.style.opacity = '1'
                                    inputElement.style.transition = 'none'
                                    inputElement.style.animation = 'none'
                                } catch (fallbackError) {}
                            }
                        }

                        // Check parent container
                        const parent = field.closest('.v-col')
                        if (parent && parent instanceof HTMLElement && parent.style) {
                            try {
                                const parentComputedStyle = window.getComputedStyle(parent)
                                if (parentComputedStyle.opacity !== '1') {
                                    parent.style.setProperty('opacity', '1', 'important')
                                    parent.style.setProperty('transition', 'none', 'important')

                                    // Force remove any transition classes from parent
                                    parent.classList.remove(
                                        'v-enter-active',
                                        'v-leave-active',
                                        'v-enter-from',
                                        'v-enter-to',
                                        'v-leave-from',
                                        'v-leave-to'
                                    )
                                }
                            } catch (e) {
                                // Fallback for parent container
                                try {
                                    parent.style.opacity = '1'
                                    parent.style.transition = 'none'
                                } catch (fallbackError) {}
                            }
                        }
                    }
                })
            }, 50) // Check every 50ms for more aggressive enforcement
        }

        get isShowConsortiumns() {
            return this.isTourOperator && (this.company.type === 'TOUR_AGENCY' || this.company.type === 'TOUR_OPERATOR')
        }

        get isTourOperator() {
            return authStore.isTourOperator
        }

        get isMyCompanyPage(): boolean {
            return authStore.person?.companyId === this.company?.id
        }

        get locale(): string {
            return this.$i18n.locale
        }

        get form(): VForm {
            return this.$refs.form as VForm
        }

        @Emit()
        async save() {
            this.isLoading = true
            let company = null

            try {
                if (this.company.id) {
                    await this.$api.companies.put(
                        this.company.id,
                        this.mapToRequest(companyInfoMessageToUpdateCompanyRQ(this.company))
                    )
                } else {
                    company = await this.$api.companies.post(
                        this.mapToRequest(companyInfoMessageToCreateCompanyRQ(this.company))
                    )

                    // Check if supplier was created but vivaAccountId is empty
                    if (
                        company &&
                        (this.company.type === 'INTERNAL_SUPPLIER' || this.company.type === 'HUB_SUPPLIER')
                    ) {
                        try {
                            // Use the correct field from POST response - companyId, not id
                            const supplierId = company.companyId || company.id

                            // Fetch the newly created supplier to get complete data including vivaAccountId
                            const supplierData = await this.$api.companies.get({id: supplierId})

                            if (supplierData.companies && supplierData.companies.length > 0) {
                                // Find the specific supplier we just created by ID
                                const createdSupplier = supplierData.companies.find(
                                    supplier => supplier.id === supplierId
                                )

                                if (createdSupplier) {
                                    // Check if vivaAccountId is missing or empty
                                    if (!createdSupplier.vivaAccountId || createdSupplier.vivaAccountId === '') {
                                        // Show error toast with manual close only (not auto-closable)
                                        this.$toast.error(this.$t('error_message_viva.viva_account_not_created'), {
                                            timeout: -1, // Never auto-close
                                            showClose: true, // Show manual close button
                                        })
                                    } else {
                                        this.company.vivaAccountId = createdSupplier.vivaAccountId
                                    }
                                } else {
                                    // If we can't find the specific supplier, assume vivaAccountId is missing
                                    this.$toast.error(this.$t('error_message_viva.viva_account_not_created'), {
                                        timeout: -1, // Never auto-close
                                        showClose: true, // Show manual close button
                                    })
                                }
                            } else {
                                // If no companies found in response, assume vivaAccountId is missing
                                this.$toast.error(this.$t('error_message_viva.viva_account_not_created'), {
                                    timeout: -1, // Never auto-close
                                    showClose: true, // Show manual close button
                                })
                            }
                        } catch (e) {
                            // If we can't fetch the supplier data, assume vivaAccountId is missing
                            this.$toast.error(this.$t('error_message_viva.viva_account_not_created'), {
                                timeout: -1, // Never auto-close
                                showClose: true, // Show manual close button
                            })
                        }
                    }
                }
                this.$toast.success(this.$t('messages.success.company_saved'))
                if (!this.company.id && !this.noRedirect) {
                    this.$router.back()
                }
            } catch (e) {
                // Check for specific supplier name validation error
                if ((e as any).errors && (e as any).errors.length > 0) {
                    const nameValidationError = (e as any).errors.find(
                        error => error.token === 'api.name.is.namdatory' && error.errorType === 'validation'
                    )

                    if (nameValidationError) {
                        // Show custom error message for supplier name validation
                        this.$toast.error(this.$t('validation.english_name_required'))
                    } else {
                        // Show generic error for other validation errors
                        this.$toast.error(this.$t('data_update_fail'))
                    }
                } else {
                    // Show generic error for non-validation errors
                    this.$toast.error(this.$t('data_update_fail'))
                }
            }
            this.isLoading = false

            return company
        }

        cancel(): void {
            const routeMap = {
                [Company.Corporator]: 'companyCorporateClients',
                [Company.HubSupplier]: 'companySuppliers',
                [Company.InternalSupplier]: 'companySuppliers',
            }

            if (this.company?.id === authStore.person?.companyId) {
                this.$router.push({name: 'home'})
            } else if (typeof this.company.type === 'string') {
                const routeName = routeMap[this.company.type] || 'companyAgencies'
                this.$router.push({name: routeName})
            } else {
                this.$router.back()
            }
        }

        mapToRequest(rq) {
            if (this.isMyCompanyPage) {
                rq.signature = this.signature ? this.signature.split(',')[1] : this.company.signature
            }
            rq.logo = this.logo ? this.logo.split(',')[1] : rq.logo
            return rq
        }

        changeActiveLangCode(code) {
            this.activeLangCode = code
        }

        get dontAllowEdit(): boolean {
            return false
        }

        get countries() {
            return runtimeStore.countries
        }

        get mapedTypes() {
            return this.types.map(type => {
                return {value: type, name: this.$t(`companyTypes.${type}`)}
            })
        }

        async loadCities(countryId): Promise<void> {
            try {
                this.isCitiesLoading = true
                const {cities} = await this.$api.locations.get({
                    limitCities: -1,
                    orderBy: 'ALPHABET',
                    countryId: countryId,
                    applyRestriction: false,
                })
                this.cities = cities
            } catch (e) {
                this.cities = []
            } finally {
                this.isCitiesLoading = false
            }
        }

        get isTO1(): boolean {
            return authStore.isTO1
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO2() {
            return authStore.isTO2
        }

        get locales() {
            const availableLocales = Object.values(this.$conf.public.locales)
            return availableLocales.map(lang => {
                return {value: lang, title: this.$t(`languages.${lang}`), icon: useFlagIconCode.value(lang)}
            })
        }

        get isCRMExtendedModule() {
            return runtimeStore.CRMSettings?.extendedCrm || false
        }

        async loadFile(fileArray: File[], target: 'signature' | 'logo'): Promise<void> {
            if (fileArray) {
                this[target] = await blobToBase64(fileArray)
            } else {
                this[target] = null
            }
        }

        removeFile(target: 'signature' | 'logo'): void {
            this[target] = null
            this.company[target] = null
        }

        get countryLabel() {
            return this.company?.type === 'INTERNAL_SUPPLIER' ? this.$t('country') : `${this.$t('country')}*`
        }

        // ZSH-402: Override validation rules for company name to include English validation for suppliers
        get companyNameRules() {
            // Use custom combined rule for suppliers with specific message
            if (this.company.type === 'INTERNAL_SUPPLIER' || this.company.type === 'HUB_SUPPLIER') {
                return [this.supplierNameRequiredRule]
            } else {
                return [this.requiredRule('company_name')]
            }
        }

        // ZSH-402: Override validation rules for email_1 to make it mandatory
        get email1Rules() {
            return [this.requiredRule('email'), this.emailRule('email')]
        }

        // ZSH-402: Combined validation rule for supplier name (required + English)
        supplierNameRequiredRule(value) {
            // First check if value is empty (required validation)
            if (!value || !value.trim()) {
                return this.$t('validation.english_name_required')
            }

            // Check if the name contains ONLY English characters, numbers, and allowed punctuation (,.-)
            const onlyAllowedChars = /^[a-zA-Z0-9,.\-\s]+$/.test(value)

            if (!onlyAllowedChars) {
                return this.$t('validation.english_name_required')
            }

            return true
        }
    }

    export default CompanyProfile
</script>

<style scoped lang="scss">
    // Ensure email fields always maintain opacity: 1 and prevent any overrides
    :deep([data-cy='email-1']),
    :deep([data-cy='email-2']),
    :deep([data-cy='email-3']) {
        opacity: 1 !important;

        // Override any transition-based opacity changes
        &,
        * {
            opacity: 1 !important;
        }

        .v-field,
        .v-field__input,
        .v-field__field,
        .v-field__overlay,
        .v-field__outline,
        .v-field__loader,
        .v-input,
        .v-input__control,
        .v-input__slot,
        input,
        label {
            opacity: 1 !important;
        }

        // Specifically target transition states to prevent opacity changes
        &.v-enter-active,
        &.v-leave-active,
        &.v-enter-from,
        &.v-enter-to,
        &.v-leave-from,
        &.v-leave-to {
            opacity: 1 !important;
        }

        // Prevent any animation or transition from changing opacity
        &,
        * {
            animation: none !important;
            transition: opacity 0s !important;
        }
    }

    // Additional global override for email field containers
    :global(.v-col:has([data-cy='email-1'])),
    :global(.v-col:has([data-cy='email-2'])),
    :global(.v-col:has([data-cy='email-3'])) {
        opacity: 1 !important;

        * {
            opacity: 1 !important;
        }
    }

    // Force override any external styles on email text fields
    :deep(.v-text-field[data-cy='email-1']),
    :deep(.v-text-field[data-cy='email-2']),
    :deep(.v-text-field[data-cy='email-3']) {
        opacity: 1 !important;

        &,
        * {
            opacity: 1 !important;
        }

        &::before,
        &::after {
            opacity: 1 !important;
        }

        // Prevent any state-based opacity changes
        &:hover,
        &:focus,
        &:active,
        &:disabled,
        &.v-field--disabled,
        &.v-field--error {
            opacity: 1 !important;
        }
    }

    // Ultimate fallback - use highest specificity to override any external styles
    :deep(.v-input[data-cy='email-1']),
    :deep(.v-input[data-cy='email-2']),
    :deep(.v-input[data-cy='email-3']) {
        opacity: 1 !important;

        .v-input__control,
        .v-input__slot,
        .v-field,
        .v-field__input,
        .v-field__field,
        .v-field__overlay,
        .v-field__outline {
            opacity: 1 !important;
        }
    }

    // Combat transition styles that set opacity: 0
    .v-col[data-cy='email-1'],
    .v-col[data-cy='email-2'],
    .v-col[data-cy='email-3'],
    :deep(.v-col[data-cy='email-1']),
    :deep(.v-col[data-cy='email-2']),
    :deep(.v-col[data-cy='email-3']) {
        opacity: 1 !important;

        &.v-enter-active,
        &.v-leave-active,
        &.v-enter-from,
        &.v-enter-to,
        &.v-leave-from,
        &.v-leave-to {
            opacity: 1 !important;
        }
    }

    // Target any element with transitions style that might affect email fields
    :deep([data-cy='email-1']),
    :deep([data-cy='email-2']),
    :deep([data-cy='email-3']) {
        // Override any inline styles or transition styles
        opacity: 1 !important;

        // Use CSS custom property to force override
        --v-field-opacity: 1 !important;

        // Disable all transitions that might affect opacity
        transition: none !important;
        animation: none !important;
    }

    // Global override for any transition styles affecting email fields
    :global(.v-col:has([data-cy='email-1'])),
    :global(.v-col:has([data-cy='email-2'])),
    :global(.v-col:has([data-cy='email-3'])) {
        opacity: 1 !important;
        transition: none !important;
        animation: none !important;

        &.v-enter-active,
        &.v-leave-active,
        &.v-enter-from,
        &.v-enter-to,
        &.v-leave-from,
        &.v-leave-to {
            opacity: 1 !important;
        }
    }

    // Nuclear option - target any element that might have transition styles affecting email fields
    [data-cy='email-1'],
    [data-cy='email-2'],
    [data-cy='email-3'],
    [data-cy='email-1'] *,
    [data-cy='email-2'] *,
    [data-cy='email-3'] * {
        opacity: 1 !important;
    }

    // Override any Vue transition classes that might be applied
    .v-enter-active[data-cy='email-1'],
    .v-enter-active[data-cy='email-2'],
    .v-enter-active[data-cy='email-3'],
    .v-leave-active[data-cy='email-1'],
    .v-leave-active[data-cy='email-2'],
    .v-leave-active[data-cy='email-3'],
    .v-enter-from[data-cy='email-1'],
    .v-enter-from[data-cy='email-2'],
    .v-enter-from[data-cy='email-3'],
    .v-enter-to[data-cy='email-1'],
    .v-enter-to[data-cy='email-2'],
    .v-enter-to[data-cy='email-3'],
    .v-leave-from[data-cy='email-1'],
    .v-leave-from[data-cy='email-2'],
    .v-leave-from[data-cy='email-3'],
    .v-leave-to[data-cy='email-1'],
    .v-leave-to[data-cy='email-2'],
    .v-leave-to[data-cy='email-3'] {
        opacity: 1 !important;
        transition: none !important;
        animation: none !important;
    }

    // Target parent containers that might have transition styles
    .v-col:has([data-cy='email-1']),
    .v-col:has([data-cy='email-2']),
    .v-col:has([data-cy='email-3']) {
        opacity: 1 !important;

        &.v-enter-active,
        &.v-leave-active,
        &.v-enter-from,
        &.v-enter-to,
        &.v-leave-from,
        &.v-leave-to {
            opacity: 1 !important;
            transition: none !important;
            animation: none !important;
        }
    }

    // CRITICAL: Override Vuetify's .v-text-field input opacity: 0 rule specifically for email fields
    .v-text-field[data-cy='email-1'] input,
    .v-text-field[data-cy='email-2'] input,
    .v-text-field[data-cy='email-3'] input,
    :deep(.v-text-field[data-cy='email-1']) input,
    :deep(.v-text-field[data-cy='email-2']) input,
    :deep(.v-text-field[data-cy='email-3']) input {
        opacity: 1 !important;
        transition: none !important;
        animation: none !important;
    }

    // Additional targeting for input elements within email fields
    [data-cy='email-1'] .v-text-field input,
    [data-cy='email-2'] .v-text-field input,
    [data-cy='email-3'] .v-text-field input,
    [data-cy='email-1'] input,
    [data-cy='email-2'] input,
    [data-cy='email-3'] input {
        opacity: 1 !important;
        transition: none !important;
        animation: none !important;
    }

    // Target any input element that might be inside email field containers
    :deep([data-cy='email-1']) .v-field__input input,
    :deep([data-cy='email-2']) .v-field__input input,
    :deep([data-cy='email-3']) .v-field__input input,
    :deep([data-cy='email-1']) .v-text-field__input input,
    :deep([data-cy='email-2']) .v-text-field__input input,
    :deep([data-cy='email-3']) .v-text-field__input input {
        opacity: 1 !important;
        transition: none !important;
        animation: none !important;
    }

    // Ultimate nuclear option for input elements
    .v-input[data-cy='email-1'] input,
    .v-input[data-cy='email-2'] input,
    .v-input[data-cy='email-3'] input,
    .v-input[data-cy='email-1'] .v-text-field input,
    .v-input[data-cy='email-2'] .v-text-field input,
    .v-input[data-cy='email-3'] .v-text-field input {
        opacity: 1 !important;
        transition: none !important;
        animation: none !important;
    }

    // EXTREME MEASURES: Use highest possible specificity to override any transition styles
    html body .v-application .v-main .v-container .v-row .v-col .v-input[data-cy='email-1'] .v-text-field input,
    html body .v-application .v-main .v-container .v-row .v-col .v-input[data-cy='email-2'] .v-text-field input,
    html body .v-application .v-main .v-container .v-row .v-col .v-input[data-cy='email-3'] .v-text-field input,
    html body .v-application .v-main .v-container .v-row .v-col [data-cy='email-1'] input,
    html body .v-application .v-main .v-container .v-row .v-col [data-cy='email-2'] input,
    html body .v-application .v-main .v-container .v-row .v-col [data-cy='email-3'] input {
        opacity: 1 !important;
        transition: none !important;
        animation: none !important;
        transform: none !important;
    }

    // Use CSS custom properties to force override
    [data-cy='email-1'],
    [data-cy='email-2'],
    [data-cy='email-3'] {
        --v-input-opacity: 1 !important;
        --v-field-opacity: 1 !important;
        --v-text-field-opacity: 1 !important;

        input {
            opacity: var(--v-input-opacity, 1) !important;
            transition: none !important;
            animation: none !important;
        }
    }

    // Target any possible transition class combinations
    .v-enter-active .v-text-field[data-cy='email-1'] input,
    .v-enter-active .v-text-field[data-cy='email-2'] input,
    .v-enter-active .v-text-field[data-cy='email-3'] input,
    .v-leave-active .v-text-field[data-cy='email-1'] input,
    .v-leave-active .v-text-field[data-cy='email-2'] input,
    .v-leave-active .v-text-field[data-cy='email-3'] input,
    .v-enter-from .v-text-field[data-cy='email-1'] input,
    .v-enter-from .v-text-field[data-cy='email-2'] input,
    .v-enter-from .v-text-field[data-cy='email-3'] input,
    .v-enter-to .v-text-field[data-cy='email-1'] input,
    .v-enter-to .v-text-field[data-cy='email-2'] input,
    .v-enter-to .v-text-field[data-cy='email-3'] input,
    .v-leave-from .v-text-field[data-cy='email-1'] input,
    .v-leave-from .v-text-field[data-cy='email-2'] input,
    .v-leave-from .v-text-field[data-cy='email-3'] input,
    .v-leave-to .v-text-field[data-cy='email-1'] input,
    .v-leave-to .v-text-field[data-cy='email-2'] input,
    .v-leave-to .v-text-field[data-cy='email-3'] input {
        opacity: 1 !important;
        transition: none !important;
        animation: none !important;
    }

    // Global override using attribute selectors with highest specificity
    [data-cy='email-1'] input,
    [data-cy='email-2'] input,
    [data-cy='email-3'] input,
    [data-cy='email-1'] .v-text-field input,
    [data-cy='email-2'] .v-text-field input,
    [data-cy='email-3'] .v-text-field input,
    [data-cy='email-1'] * input,
    [data-cy='email-2'] * input,
    [data-cy='email-3'] * input {
        opacity: 1 !important;
        transition: none !important;
        animation: none !important;
    }

    .viva-account-id-readonly {
        opacity: 0.6;
        pointer-events: auto;

        :deep(input) {
            cursor: text;
            user-select: all;
        }
    }
</style>
